import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import { AITransitionProvider } from "@/components/AITransition";
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Relationships from "@/pages/Relationships";
import RelationshipDetail from "@/pages/RelationshipDetail";
import CalendarPage from "@/pages/Calendar";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import ExperienceDetail from "@/pages/ExperienceDetail";
import Checkout from "@/pages/Checkout";
import Bookings from "@/pages/Bookings";
import Admin from "@/pages/Admin";
import Planner from "@/pages/Planner";
import Profile from "@/pages/Profile";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function Protected({ children, adminOnly = false, LayoutComp = Layout }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-linen"><div className="w-10 h-10 rounded-full ai-glow" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <LayoutComp>{children}</LayoutComp>;
}

function AppRouter() {
  const location = useLocation();
  // Emergent Google OAuth returns with session_id in URL hash — handle synchronously
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/relationships" element={<Protected><Relationships /></Protected>} />
      <Route path="/relationships/:id" element={<Protected><RelationshipDetail /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/shop" element={<Protected><Shop /></Protected>} />
      <Route path="/product/:id" element={<Protected><ProductDetail /></Protected>} />
      <Route path="/experiences/:id" element={<Protected><ExperienceDetail /></Protected>} />
      <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
      <Route path="/bookings" element={<Protected><Bookings /></Protected>} />
      <Route path="/orders" element={<Protected><Bookings /></Protected>} />
      <Route path="/planner" element={<Protected><Planner /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly LayoutComp={AdminLayout}><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AITransitionProvider>
            <AppRouter />
          </AITransitionProvider>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
